import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  Boxes,
  Building2,
  Factory,
  MessageCircle,
  Phone,
  Ruler,
  ShieldCheck,
  Timer,
  Truck,
  ClipboardList,
} from "lucide-react";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { SafeImage } from "@/components/SafeImage";

import { APPLICATIONS, COMPANY, FAQS, IMAGES, PRODUCT_CATEGORIES, SEO } from "@/mock";
import { CategoryCard, SectionTitle, StatPill } from "@/pages/_bits";
import { Container, makeTelUrl, makeWhatsAppUrl, setTitle } from "@/pages/_shared";

export default function HomePage() {
  const navigate = useNavigate();

  useEffect(() => {
    setTitle("Steel Wires, Fencing & Geotechnical Solutions");
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(16,185,129,0.12),transparent_55%),radial-gradient(circle_at_80%_30%,rgba(245,158,11,0.10),transparent_55%)]" />
          <div className="absolute inset-0 noise" />
        </div>

        <Container className="py-14 sm:py-20">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex flex-wrap items-center gap-2">
                <Badge className="bg-emerald-100 text-emerald-950 border-emerald-200">
                  {COMPANY.certification}
                </Badge>
                <Badge className="bg-amber-100 text-amber-950 border-amber-200">
                  {COMPANY.years}+ years
                </Badge>
                <Badge variant="secondary" className="bg-secondary/60">
                  Guwahati manufacturing
                </Badge>
              </div>

              <h1 className="mt-5 text-3xl sm:text-5xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                Trusted Manufacturer & Supplier of <span className="text-emerald-700">Steel Wires</span>,
                <span className="text-slate-900 dark:text-white"> Fencing</span> &
                <span className="text-amber-700"> Geotechnical Solutions</span> in Northeast India
              </h1>

              <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl">
                Based in <strong>{COMPANY.locationShort}</strong>, we support infrastructure contractors,
                government supply requirements, EPCs and distributors with quality-controlled manufacturing
                and dependable dispatch.
              </p>

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Button
                  className="bg-amber-500 text-slate-950 hover:bg-amber-400"
                  onClick={() => navigate("/request-quote")}
                >
                  Request a Quote <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  className="border-emerald-200/60 bg-emerald-50 text-emerald-950 hover:bg-emerald-100"
                  onClick={() => window.open(makeWhatsAppUrl(COMPANY.whatsapp, "Hello, I need a quotation."), "_blank")}
                >
                  <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Us
                </Button>
                <Button variant="outline" onClick={() => (window.location.href = makeTelUrl(COMPANY.phone))}>
                  <Phone className="mr-2 h-4 w-4" /> Call Now
                </Button>
              </div>

              <div className="mt-10 grid gap-3 sm:grid-cols-3">
                <StatPill icon={Timer} title="Fast RFQ response" desc="Clear, buyer-ready quotations" />
                <StatPill icon={Ruler} title="Custom sizes" desc="Project specs supported" />
                <StatPill icon={Truck} title="Northeast delivery" desc="Reliable dispatch planning" />
              </div>

              <div className="mt-8 space-y-1 text-xs text-muted-foreground">
                <div>
                  SEO focus: {SEO.keywords.slice(0, 3).join(" • ")} • Northeast India: {SEO.northeastStates.join(", ")}
                </div>
                <div>
                  Cities: {SEO.majorCities.slice(0, 12).join(", ")}
                  {SEO.majorCities.length > 12 ? "" : ""}
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative overflow-hidden rounded-3xl border border-border/60 bg-background/70 shadow-sm">
                <div className="aspect-[4/3]">
                  <SafeImage
                    src={IMAGES.hero}
                    alt="Industrial wire manufacturing"
                    imgClassName="h-full w-full object-cover"
                    fallbackLabel="Industrial manufacturing"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-xl">
                    <div className="text-white text-sm font-bold tracking-tight">
                      Need pricing for bulk supply?
                    </div>
                    <div className="mt-1 text-white/80 text-xs">
                      Share product + diameter/mesh + quantity—get a fast quote.
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button
                        size="sm"
                        className="bg-amber-500 text-slate-950 hover:bg-amber-400"
                        onClick={() => navigate("/request-quote")}
                      >
                        Request Quote
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-white/25 bg-white/10 text-white hover:bg-white/15"
                        onClick={() => window.open(makeWhatsAppUrl(COMPANY.whatsapp, "Hello, please share pricing."), "_blank")}
                      >
                        WhatsApp
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-border/60 bg-secondary/40 p-4">
                  <div className="flex items-center gap-2 text-sm font-extrabold tracking-tight">
                    <BadgeCheck className="h-4 w-4 text-emerald-700" /> Quality-controlled
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Material inspection, coating checks and strength verification for dependable supply.
                  </p>
                </div>
                <div className="rounded-2xl border border-border/60 bg-secondary/40 p-4">
                  <div className="flex items-center gap-2 text-sm font-extrabold tracking-tight">
                    <Building2 className="h-4 w-4 text-slate-700" /> Contractor-ready supply
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Bulk orders, repeat supply schedules and packaging suitable for site handling.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Intro */}
      <section className="py-14">
        <Container>
          <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-5">
              <SectionTitle
                eyebrow="National Wire Products"
                title="Built for infrastructure supply—quality, consistency, accountability"
                desc={`With ${COMPANY.years}+ years of experience, we manufacture and supply steel wire products, fencing solutions and geotechnical structures for Northeast India. We support government and private projects with timely dispatch and custom specifications.`}
              />

              <div className="mt-6 grid gap-3">
                <StatPill icon={Factory} title="Manufacturing in Guwahati" desc="Controlled process + steady supply" />
                <StatPill icon={ShieldCheck} title="ISO-aligned process" desc="Quality checks and documentation" />
                <StatPill icon={Boxes} title="Bulk supply capability" desc="Contractor + distributor orders" />
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="overflow-hidden rounded-3xl border border-border/60">
                  <SafeImage src={IMAGES.welding} alt="Manufacturing" imgClassName="h-64 w-full object-cover" fallbackLabel="Manufacturing" />
                </div>
                <div className="overflow-hidden rounded-3xl border border-border/60">
                  <SafeImage src={IMAGES.quality} alt="Quality testing" imgClassName="h-64 w-full object-cover" fallbackLabel="Quality testing" />
                </div>
                <div className="sm:col-span-2 overflow-hidden rounded-3xl border border-border/60">
                  <SafeImage src={IMAGES.facility} alt="Industrial facility" imgClassName="h-64 w-full object-cover" fallbackLabel="Industrial facility" />
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Categories */}
      <section className="py-14 bg-secondary/25">
        <Container>
          <SectionTitle
            eyebrow="Products"
            title="Steel wires, fencing products & geotechnical structures"
            desc="3 clear categories—open any product page for typical specifications, features and use cases."
          />

          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {PRODUCT_CATEGORIES.map((c) => (
              <CategoryCard key={c.id} c={c} />
            ))}
          </div>
        </Container>
      </section>

      {/* Why choose */}
      <section className="py-14">
        <Container>
          <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-5">
              <SectionTitle
                eyebrow="Why choose us"
                title="Manufacturing discipline—plus contractor-friendly execution"
                desc="We focus on consistency, documentation and supply reliability—so your site work doesn’t pause."
              />
            </div>
            <div className="lg:col-span-7">
              <div className="grid gap-4 sm:grid-cols-2">
                <StatPill icon={BadgeCheck} title="ISO-compliant manufacturing" desc="Process-focused quality controls" />
                <StatPill icon={ShieldCheck} title="Premium raw materials" desc="Cleaner finish and stable performance" />
                <StatPill icon={ClipboardList} title="Custom sizes & specs" desc="Align to BOQ, drawings & site needs" />
                <StatPill icon={Truck} title="Timely delivery" desc="Planned dispatch across Northeast India" />
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Applications */}
      <section className="py-14 bg-secondary/25">
        <Container>
          <SectionTitle
            eyebrow="Applications"
            title="Used across infrastructure, security, agriculture and civil works"
            desc="Typical uses across the Northeast—share your application for the right configuration."
          />

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {APPLICATIONS.map((a) => (
              <Card key={a.title} className="border-border/60 bg-background/70">
                <CardHeader>
                  <CardTitle className="text-base font-extrabold tracking-tight">{a.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">{a.desc}</CardContent>
              </Card>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA Banner */}
      <section className="py-14">
        <Container>
          <div className="relative overflow-hidden rounded-3xl border border-border/60 bg-slate-950 p-8 sm:p-10 text-slate-50">
            <div className="absolute inset-0 opacity-50 bg-[radial-gradient(circle_at_20%_10%,rgba(16,185,129,0.25),transparent_55%),radial-gradient(circle_at_80%_40%,rgba(245,158,11,0.18),transparent_55%)]" />
            <div className="absolute inset-0 noise opacity-30" />

            <div className="relative grid gap-8 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-8">
                <div className="text-xs font-bold tracking-widest text-emerald-200 uppercase">
                  Bulk supply • Custom specs • Northeast delivery
                </div>
                <h3 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight">
                  Looking for reliable wire & fencing solutions? Get a quote today.
                </h3>
                <p className="mt-3 text-sm sm:text-base text-slate-200 max-w-2xl">
                  Share product, size/diameter/mesh, coating type, location and quantity. We’ll respond with pricing and lead time.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3">
                <Button className="bg-amber-500 text-slate-950 hover:bg-amber-400" onClick={() => navigate("/request-quote")}>
                  Request a Quote <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  className="border-white/25 bg-white/10 text-white hover:bg-white/15"
                  onClick={() => window.open(makeWhatsAppUrl(COMPANY.whatsapp, "Hello, please share a quotation."), "_blank")}
                >
                  <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Us
                </Button>
                <Button
                  variant="outline"
                  className="border-white/25 bg-white/10 text-white hover:bg-white/15"
                  onClick={() => (window.location.href = makeTelUrl(COMPANY.phone))}
                >
                  <Phone className="mr-2 h-4 w-4" /> Call Now
                </Button>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-secondary/25">
        <Container>
          <SectionTitle
            eyebrow="FAQ"
            title="Quick answers for procurement and site engineers"
            desc="If you have a BOQ or drawing, send it—we’ll match the right product configuration."
          />

          <div className="mt-10 rounded-3xl border border-border/60 bg-background/70 p-4 sm:p-6">
            <Accordion type="single" collapsible>
              {FAQS.map((f, idx) => (
                <AccordionItem key={idx} value={`faq-${idx}`} className="border-border/60">
                  <AccordionTrigger className="text-base font-semibold no-underline hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div className="mt-10 text-sm text-muted-foreground">
            Prefer a quick quote? Go to <Link className="font-semibold text-foreground hover:underline" to="/request-quote">Request a Quote</Link>.
          </div>
        </Container>
      </section>
    </div>
  );
}
